import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-drafts-form',
  templateUrl: './dashboard-drafts-form.component.html',
  styleUrls: ['./dashboard-drafts-form.component.scss'],
})
export class DashboardDraftsFormComponent implements OnInit {
  form = this.formBuilder.group({
    name: [null, [Validators.required]],
    type: [null, [Validators.required]],

    final_date: [
      null,
      [
        Validators.required,
        Utils.validateMaxDate,
        (control: FormControl) => Utils.validateMaxMinToday(control, false),
      ],
    ],
    enabled: [true, []],

    content: [null, [Validators.required]],
  });

  searchForm = this.formBuilder.group({
    search: [null, []],
  });

  lineForm = this.formBuilder.group({
    role: [null, []],
  });

  applicableItems = [];
  availableRoles = [];
  visibleRoles = [];

  coobligationItems = [
    { label: 'Sem coobrigação', value: false },
    { label: 'Com coobrigação', value: true },
  ];

  typeItems = [];

  statusItems = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  id = null;
  fundId = null;

  templateId = null;

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      ['clean'],
    ],
  };

  assignees: any[] = [];
  types: any[] = [];
  showForm = true;
  lineModal = false;
  showResume = false;

  loading = true;

  title = '';

  variables: VariableValues[] = [];
  count = 0;
  offset = 0;
  results_per_page = 10;
  loadingVariables = true;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private routeParams: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private toast: ToastService,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    if (
      (this.router.url.includes('new') &&
        !this.authService.verifyPermission(['de_can_add_drafts'])) ||
      (this.route.snapshot.params.id &&
        !this.authService.verifyPermission(['de_can_edit_drafts']))
    ) {
      this.toast.show(
        'error',
        'Aviso',
        'Você não tem permissão para acessar essa página.'
      );
      this.router.navigateByUrl('/app/dashboard');
    }

    this.setBreadcrumb();

    this.form.valueChanges.subscribe((values) => {
      Utils.getErrors(this.form);
    });

    this.searchForm.controls.search.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((searchValue) => {
        this.loadingVariables = true;
        this.getVariables(1, searchValue);
      });

    this.fetchData();

    this.route.params.subscribe(({ id, fundId }) => {
      if (!this.router.url.includes('new')) {
        this.id = this.routeParams.snapshot.params.id;
        this.setData();
      } else {
        this.id = null;
      }
    });

    this.getVariables();
  }

  async fetchData() {
    await Promise.all([this.fetchRoles(), this.fetchTypes()]);

    this.loading = false;
  }

  async fetchRoles() {
    try {
      const response = await this.api.get<Role[]>({
        route: 'api/roles',
        token: true,
      });

      const acceptedApplicables = ['shareholder', 'assignor', 'provider'];

      const mappedData = response
        .filter((item) => acceptedApplicables.includes(item.applicable))
        .map((item) => ({
          label: `${item.applicable_display} - ${item.name}`,
          value: item.id,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      this.applicableItems = mappedData;
      this.availableRoles = [...mappedData];
      this.updateVisibleRoles();
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de papeis.'
      );
    }
  }

  async fetchTypes() {
    try {
      const { data } = await this.api.get<ApiResponse<any[]>>({
        route: 'api/document-type',
        token: true,
      });

      this.types = data;

      const mappedData = data
        .map((item) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        });

      this.typeItems = mappedData;
    } catch (error) {
      console.log(error);

      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os dados de tipos de documento.'
      );
    }
  }

  async setData() {
    try {
      const { data } = await this.api.get({
        route: `api/draft/${this.id}/`,
        token: true,
      });

      this.form.patchValue({
        ...data,
        type: data.type.id,
        final_date: data.final_date
          ? data.final_date.split('-').reverse().join('/')
          : null,
      });

      if (data.assignees && data.assignees.length > 0) {
        this.assignees = data.assignees.map((assigneeId) => {
          const role = this.applicableItems.find(
            (item) => item.value === assigneeId
          );
          return {
            id: assigneeId,
            label: role ? role.label : 'Papel não encontrado',
          };
        });
        this.updateVisibleRoles();
      }

      this.title = data.name;
    } catch (error) {
      console.error(error);
    }
  }
  getBackLink() {
    if (this.fundId) {
      return `/app/funds/approval/${this.fundId}`;
    } else {
      return '/app/drafts-old';
    }
  }

  redirect() {
    if (this.fundId) {
      this.router.navigate([`/app/funds/approval/${this.fundId}`], {
        state: { step: 9 },
      });
    } else {
      this.router.navigate(['/app/drafts/models']);
    }
  }

  getDisabled() {
    return !this.form.valid;
  }

  async createTemplate(isDraft = false) {
    try {
      const payload = {
        ...this.form.value,
        final_date: this.form.controls.final_date.value
          ? this.form.controls.final_date.value.split('/').reverse().join('-')
          : null,
        is_draft: isDraft,
        assignees: this.assignees.map((assignee) => assignee.id),
      };

      let response = null;

      if (this.id) {
        response = await this.api.patch({
          route: `api/draft/${this.id}/`,
          token: true,
          body: payload,
        });
      } else {
        response = await this.api.post({
          route: `api/draft/`,
          token: true,
          body: payload,
        });
      }

      this.toast.show(
        'info',
        'Sucesso',
        this.id
          ? 'Template atualizado, já está disponível para ser utilizado pelo sistema'
          : 'Um novo template foi criado e já está disponível para ser utilizado pelo sistema'
      );

      const { data } = response;

      this.redirect();
    } catch (error) {
      console.error(error);
    }
  }

  async getVariables(page = 1, search?: string) {
    try {
      const params: any = {
        page: page,
      };

      if (search && search.trim() !== '') {
        params.search = search.trim();
      }

      const { results, count, offset, results_per_page } = await this.api.get({
        route: 'api/variables/',
        token: true,
        params: params,
      });

      this.count = count;
      this.offset = offset;
      this.results_per_page = results_per_page;
      this.variables = results;
    } catch (error) {
      console.log(error);
    }

    this.loadingVariables = false;
  }

  changePageData(page) {
    this.loadingVariables = true;
    const searchValue = this.searchForm.controls.search.value;
    this.getVariables(page, searchValue);
  }

  showLineModal() {
    this.lineModal = true;
  }

  handleBack() {
    if (this.showResume) {
      this.showResume = false;
      this.showForm = false;
    } else {
      this.showForm = true;
      this.showResume = false;
    }
  }

  handleNext() {
    if (this.showResume) {
      this.createTemplate();
      return;
    }

    if (this.showForm) {
      this.showForm = false;
    } else {
      this.showResume = true;
    }
  }

  renderType(typeId: number) {
    const type = this.typeItems.find((item) => item.value === typeId);
    return type ? type.label : '-';
  }

  addAssignees() {
    const selectedRoles = this.lineForm.controls.role.value;

    if (!selectedRoles || selectedRoles.length === 0) {
      return;
    }

    selectedRoles.forEach((roleId) => {
      const role = this.applicableItems.find((item) => item.value === roleId);
      if (role && !this.assignees.find((assignee) => assignee.id === roleId)) {
        this.assignees.push({
          id: roleId,
          label: role.label,
        });
      }
    });

    this.updateVisibleRoles();
    this.lineForm.reset();
    this.lineModal = false;
  }

  removeAssignee(index: number) {
    this.assignees.splice(index, 1);
    this.updateVisibleRoles();
  }

  updateVisibleRoles() {
    const assignedRoleIds = this.assignees.map((assignee) => assignee.id);
    this.visibleRoles = this.availableRoles.filter(
      (item) => !assignedRoleIds.includes(item.value)
    );
  }

  getAvailableRoles() {
    const assignedRoleIds = this.assignees.map((assignee) => assignee.id);
    return this.applicableItems.filter(
      (item) => !assignedRoleIds.includes(item.value)
    );
  }

  insertVariable(variable: string) {
    const currentContent = this.form.controls.content.value || '';
    this.form.controls.content.setValue(currentContent + variable);
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const isNew = this.router.url.includes('new');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: '/app/drafts',
        clickable: canViewDrafts,
      },
      {
        label: 'Templates de minutas',
        url: '/app/drafts/models',
        clickable: canViewDrafts,
      },
      {
        label: isNew ? 'Novo modelo' : 'Editar modelo',
        url: this.router.url,
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
